import React, { useEffect, useState } from 'react';
import axios from '../config/api';
import { formatCurrency } from '../utils/format';
import { downloadExcelSheet } from '../utils/downloadExcel';

type Totals = {
  label?: string;
  from?: string | null;
  to?: string | null;
  order_count: number;
  sales_jod: number;
  discount_jod: number;
  commission_recorded_jod: number;
  commission_charged_jod: number;
};

type CafeRow = Totals & {
  cafe_id: string;
  cafe_name: string;
  commission_percent: number;
};

type Report = {
  cafe_id: string | null;
  is_superuser: boolean;
  timezone: string;
  commission_note: string;
  periods: { today: Totals; week: Totals; month: Totals; all_time: Totals };
  range: Totals & { cafes: CafeRow[] };
};

const RANGES = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'This week' },
  { id: 'month', label: 'This month' },
  { id: 'all', label: 'All time' },
  { id: 'custom', label: 'Date range' },
] as const;

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border border-amber-100 bg-white px-3 py-3">
      <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="mt-1 text-lg font-bold text-amber-950 tabular-nums">{value}</p>
      {hint && <p className="text-[11px] text-gray-500">{hint}</p>}
    </div>
  );
}

export default function DineInReportPanel({
  cafeId,
  isSupportAdmin,
}: {
  cafeId: string;
  isSupportAdmin: boolean;
}) {
  const [range, setRange] = useState<(typeof RANGES)[number]['id']>('month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSupportAdmin && !cafeId) return;
    let cancelled = false;
    const params = new URLSearchParams({ range });
    if (cafeId) params.set('cafe_id', cafeId);
    if (range === 'custom') {
      if (!from || !to) return;
      params.set('from', from);
      params.set('to', to);
    }
    setLoading(true);
    axios
      .get<Report>(`/api/reports/dine-in?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setReport(res.data);
          setError('');
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || 'Could not load dine-in report');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cafeId, from, isSupportAdmin, range, to]);

  const periods = report?.periods;

  return (
    <section className="bg-white rounded-lg shadow-md p-6 border border-gray-100 mb-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
      <h2 className="text-lg font-bold text-amber-800">Dine-in orders</h2>
      <button
        type="button"
        disabled={!report}
        onClick={() => {
          if (!report) return;
          const rows =
            report.range.cafes.length === 0
              ? [{ note: 'No dine-in orders in this range.' }]
              : report.range.cafes.map((cafe) => ({
                  cafe: cafe.cafe_name,
                  orders: cafe.order_count,
                  sales_jod: cafe.sales_jod.toFixed(3),
                  discount_jod: cafe.discount_jod.toFixed(3),
                  commission_recorded_jod: cafe.commission_recorded_jod.toFixed(3),
                  commission_collected_jod: cafe.commission_charged_jod.toFixed(3),
                }));
          downloadExcelSheet(
            `dine-in-${report.range.from || 'all'}-${report.range.to || 'time'}.xlsx`,
            'Dine-in',
            rows
          );
        }}
        className="rounded-lg bg-[#3d2918] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
      >
        Excel
      </button>
      </div>
      <p className="mt-1 text-sm text-gray-600">
        {isSupportAdmin
          ? 'Every café, or the one selected under Location.'
          : 'Your café only.'}{' '}
        Dates use Jordan time. Totals are by café. Cancelled orders are not included. Sales are what the customer paid.
      </p>
      {report?.commission_note && (
        <p className="mt-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-950">
          Commission: {report.commission_note}
        </p>
      )}

      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Today" value={String(periods?.today.order_count ?? '–')} hint="orders" />
        <Stat label="This week" value={String(periods?.week.order_count ?? '–')} hint="Mon–today" />
        <Stat label="This month" value={String(periods?.month.order_count ?? '–')} hint="orders" />
        <Stat label="All time" value={String(periods?.all_time.order_count ?? '–')} hint="orders" />
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-2">
        {RANGES.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setRange(item.id)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${
              range === item.id ? 'bg-amber-800 text-white' : 'bg-amber-50 text-amber-900'
            }`}
          >
            {item.label}
          </button>
        ))}
        {range === 'custom' && (
          <>
            <label className="text-xs text-gray-600">
              From
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="mt-1 block rounded border border-gray-300 px-2 py-1"
              />
            </label>
            <label className="text-xs text-gray-600">
              To
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="mt-1 block rounded border border-gray-300 px-2 py-1"
              />
            </label>
          </>
        )}
      </div>

      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
      {loading && <p className="mt-3 text-sm text-gray-500">Loading…</p>}

      {report && (
        <div className="mt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Stat label="Orders in range" value={String(report.range.order_count)} />
            <Stat label="Dine-in sales" value={formatCurrency(report.range.sales_jod)} />
            <Stat label="Discounts given" value={formatCurrency(report.range.discount_jod)} />
            <Stat
              label="Commission recorded"
              value={formatCurrency(report.range.commission_recorded_jod)}
              hint={`Collected ${formatCurrency(report.range.commission_charged_jod)}`}
            />
          </div>

          {report.range.cafes.length === 0 ? (
            <p className="text-sm text-gray-500">No dine-in orders in this range.</p>
          ) : (
            <div className="space-y-4">
              {report.range.cafes.map((cafe) => (
                <div key={cafe.cafe_id} className="rounded-lg border border-gray-200">
                  <div className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2 bg-gray-50">
                    <p className="font-semibold text-gray-900">
                      {cafe.cafe_name}
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        {cafe.commission_percent}% commission
                      </span>
                    </p>
                    <p className="text-xs text-gray-600">
                      {cafe.order_count} orders · {formatCurrency(cafe.sales_jod)} sales ·{' '}
                      {formatCurrency(cafe.discount_jod)} off ·{' '}
                      {formatCurrency(cafe.commission_recorded_jod)} commission
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
